import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Briefcase, Handshake, LineChart, Globe, Anchor, HardHat } from 'lucide-react';

export const ServicesSection: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(containerRef, { once: true, margin: "-100px" });

  const services = [
    {
      icon: <Briefcase className="w-8 h-8" />,
      title: "End-to-End Advisory & Brokerage",
      description: "Full-lifecycle strategic advisory and brokerage services across natural resources, commodities, and hospitality sectors. From origination to execution, we guide clients through complex, capital-intensive markets to maximize value and manage risk.",
    },
    {
      icon: <Handshake className="w-8 h-8" />,
      title: "Structuring & Facilitation",
      description: "Leveraging our global network to facilitate unique project requirements. We enable long-term strategic partnerships that accelerate growth and strengthen balance sheets, arranging tailored solutions that traditional channels often cannot accommodate.",
    },
    {
      icon: <LineChart className="w-8 h-8" />,
      title: "Technical & Commercial Solutions",
      description: "Combining rigorous technical expertise with commercial acumen. We conduct in-depth technical due diligence, market and logistics analyses, and risk-reward structuring spanning project development, operations, and deal execution.",
    }
  ];

  return (
    <section id="services" ref={containerRef} className="py-24 bg-zinc-50">
      <div className="container mx-auto px-6">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <motion.h4 
            className="text-amber-500 font-bold uppercase tracking-widest text-sm mb-2"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            What We Do
          </motion.h4>
          <motion.h2 
            className="text-3xl md:text-4xl font-bold text-zinc-900 mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            Comprehensive Solutions for Complex Markets
          </motion.h2>
          <motion.p 
            className="text-zinc-600 text-lg"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            We deliver strategic guidance and transaction support designed to maximize value and mitigate risk in capital-intensive markets.
          </motion.p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <motion.div
              key={index}
              className="bg-white p-8 rounded-sm shadow-sm border-b-4 border-transparent hover:border-amber-500 transition-all duration-300 group"
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.3 + index * 0.1 }}
            >
              <div className="w-16 h-16 bg-zinc-100 text-zinc-900 rounded-full flex items-center justify-center mb-6 group-hover:bg-zinc-900 group-hover:text-white transition-colors duration-300">
                {service.icon}
              </div>
              <h3 className="text-xl font-bold text-zinc-900 mb-4 group-hover:text-amber-600 transition-colors">
                {service.title}
              </h3>
              <p className="text-zinc-600 leading-relaxed">
                {service.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};